import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import MainNav from '../components/MainNav';
import MainFooter from '../components/MainFooter';
import PhotoCarousel from '../components/PhotoCarousel';
import { usePageTracking } from '../hooks/useAnalytics';
import { 
    Heart, 
    ArrowRight, 
    Check,
    Sparkles,
    Users,
    Calendar,
    Lightbulb,
    Shield,
    Target,
    ExternalLink,
    ChevronDown,
    MessageSquare,
    Linkedin,
    Globe
} from 'lucide-react';

// Founder photo URL
const FOUNDER_PHOTO = "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/dn2b2ve3_image.png";

// Carousel images
const CAROUSEL_IMAGES = [
    {
        url: "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/hleasgdm_83172bd6-4106-41df-909d-76f9a1b9de43~1.jpg",
        caption: "Brotherhood. Growth. Purpose."
    },
    {
        url: "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/x9z4c50x_1743952321392~2.jpg",
        caption: "Building community through authentic connection."
    },
    {
        url: "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/1kohggo2_1759629804404~3.jpg",
        caption: "A safe space to share, listen, and grow together."
    },
    {
        url: "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/66ntn1ft_Screenshot_20251215-010154.png",
        caption: "When Black men support each other, transformation happens."
    },
    {
        url: "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/vq1i80dw_Screenshot_20251215-042504.png",
        caption: "Recovery Room - Where healing begins."
    }
];

const JoinPage = () => {
    const navigate = useNavigate();
    
    // Track page view
    usePageTracking('Home');

    return (
        <div className="min-h-screen bg-[#3D4435]">
            <MainNav />
            
            {/* Hero Section */}
            <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
                <div 
                    className="absolute inset-0 z-0"
                    style={{
                        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/uwuwlxdg_1763189042442.jpg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center 35%'
                    }}
                />
                
                {/* Option B: Subtle horizontal gradient band behind text */}
                <div 
                    className="absolute inset-x-0 z-10 pointer-events-none"
                    style={{
                        top: '15%',
                        bottom: '10%',
                        background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.5) 20%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.5) 80%, transparent 100%)'
                    }}
                />
                
                <div className="relative z-20 max-w-4xl mx-auto px-6 text-center pt-20">
                    <h1 className="text-5xl sm:text-7xl lg:text-8xl font-bold text-white font-['Playfair_Display'] mb-5 leading-tight drop-shadow-lg">
                        Recovery Room
                    </h1>
                    <p className="text-2xl sm:text-3xl text-white font-light mb-4 drop-shadow-md">
                        A safe space for Black men.
                    </p>
                    <p className="text-lg text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed drop-shadow-md">
                        Where healing begins, brotherhood thrives, and purpose is discovered. 
                        Join a community dedicated to the mental and emotional well-being of Black&nbsp;men.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button
                            size="lg"
                            onClick={() => navigate('/member/register')}
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold px-8 py-6 text-lg"
                            data-testid="join-community-btn"
                        >
                            Become a Member
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            onClick={() => window.open('https://www.eventbrite.com/e/recovery-room-saturdays-tickets-1964427458409', '_blank')}
                            className="bg-white text-[#3D4435] hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
                            data-testid="register-rr-saturdays-btn"
                        >
                            Register for R&R Saturdays
                        </Button>
                        <Button
                            size="lg"
                            variant="outline"
                            onClick={() => window.open('https://www.paypal.com/donate/?hosted_button_id=F3BSD37A8WSJQ', '_blank')}
                            className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
                            data-testid="donate-btn"
                        >
                            Support Our Mission
                        </Button>
                    </div>
                </div>
                
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 animate-bounce">
                    <ChevronDown className="w-8 h-8 text-white/50" />
                </div>
            </section>

            {/* Who We Are Section */}
            <section className="py-24 px-6 bg-[#3D4435]">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                                Who We Are
                            </p>
                            <h2 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display'] mb-8 leading-tight">
                                A Sanctuary for the Mind, Body & Spirit
                            </h2>
                            <div className="space-y-6 text-[#A0A0A0] leading-relaxed">
                                <p>
                                    Recovery Room (R&R) is a wellness service initiative created to support the mental and emotional well-being of Black men. Through monthly gatherings, wellness sessions, training and development opportunities, and intentional community connections, R&R offers a safe, welcoming space for positive reflection, healing, growth, and community support.
                                </p>
                                <p>
                                    Too often, Black men shoulder the world's expectations without a space to release, reset, or simply be. The Recovery Room was created to change that.
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            {[
                                { icon: Shield, text: "Be seen, valued, and supported" },
                                { icon: Users, text: "Connect with purpose and community" },
                                { icon: Sparkles, text: "Explore positive outlets and creative expression" },
                                { icon: Heart, text: "Access culturally intentional wellness practices" },
                                { icon: Target, text: "Build relationships that strengthen identity and resilience" }
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-center gap-4 p-4 bg-[#4A5240] rounded-xl border border-[#5A6350]">
                                    <div className="w-12 h-12 rounded-full bg-[#C9A227]/10 flex items-center justify-center flex-shrink-0">
                                        <item.icon className="w-6 h-6 text-[#C9A227]" />
                                    </div>
                                    <p className="text-white font-medium">{item.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-24 px-6 bg-[#4A5240]">
                <div className="max-w-6xl mx-auto">
                    {/* Photo Carousel */}
                    <div className="mb-16">
                        <PhotoCarousel images={CAROUSEL_IMAGES} autoPlay={true} interval={6000} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <Card className="bg-[#3D4435] border-[#5A6350] overflow-hidden">
                            <div className="h-1 bg-[#C9A227]" />
                            <CardContent className="p-8">
                                <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-3">
                                    Our Mission
                                </p>
                                <p className="text-2xl text-white font-['Playfair_Display'] leading-relaxed">
                                    To curate safe spaces for Black men to connect, find purpose, and align within community.
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="bg-[#3D4435] border-[#5A6350] overflow-hidden">
                            <div className="h-1 bg-[#C9A227]" />
                            <CardContent className="p-8">
                                <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-3">
                                    Our Vision
                                </p>
                                <p className="text-2xl text-white font-['Playfair_Display'] leading-relaxed">
                                    To improve the overall mental wellness of Black men, one room and one experience at a time.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-24 px-6 bg-[#3D4435]">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            What We Offer
                        </p>
                        <h2 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display'] mb-6">
                            Services & Initiatives
                        </h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { 
                                icon: Calendar, 
                                title: "R&R Saturdays", 
                                desc: "Monthly gatherings for connection, introspection, and community. Every 1st Saturday.",
                                cta: "Register"
                            },
                            { 
                                icon: Lightbulb, 
                                title: "Mindfulness Meditation", 
                                desc: "Online sessions for mental clarity and emotional balance.",
                                cta: "Join Online"
                            },
                            { 
                                icon: MessageSquare, 
                                title: "Talk Out Loud", 
                                desc: "Group sessions where brothers share, listen, and support one another.",
                                cta: "Learn More"
                            },
                            { 
                                icon: Target, 
                                title: "Recovered Dreams", 
                                desc: "Empowerment experiences to rediscover purpose and align with your goals.",
                                cta: "Explore"
                            }
                        ].map((service, idx) => (
                            <Card key={idx} className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all duration-300 group">
                                <CardContent className="p-6">
                                    <div className="w-14 h-14 rounded-xl bg-[#C9A227]/10 flex items-center justify-center mb-5 group-hover:bg-[#C9A227]/20 transition-colors">
                                        <service.icon className="w-7 h-7 text-[#C9A227]" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                                    <p className="text-[#A0A0A0] text-sm mb-5 leading-relaxed">{service.desc}</p>
                                    <Button 
                                        variant="ghost" 
                                        className="text-[#C9A227] hover:text-[#E8D48A] p-0 h-auto font-medium"
                                        onClick={() => window.open('https://www.recoveredbrother.com/', '_blank')}
                                    >
                                        {service.cta}
                                        <ExternalLink className="w-4 h-4 ml-2" />
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Building Fund Section */}
            <section className="py-24 px-6 bg-gradient-to-b from-[#4A5240] to-[#3D4435]">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                                Help Us Grow
                            </p>
                            <h2 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display'] mb-6 leading-tight">
                                The Building Fund Campaign
                            </h2>
                            <p className="text-[#A0A0A0] mb-8 leading-relaxed">
                                We are raising funds to establish our permanent home — a flagship Recovery Room facility in Washington, DC. This will be the nation's first universal wellness center designed specifically for Black men.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                                {[
                                    "Quiet room for meditation",
                                    "Music & art room",
                                    "Reading & resource lounge",
                                    "Game & film room",
                                    "Journaling space",
                                    "Therapy & coaching"
                                ].map((feature, idx) => (
                                    <div key={idx} className="flex items-center gap-3">
                                        <Check className="w-5 h-5 text-[#C9A227]" />
                                        <span className="text-white">{feature}</span>
                                    </div>
                                ))}
                            </div>
                            <Button
                                size="lg"
                                onClick={() => window.open('https://www.paypal.com/donate/?hosted_button_id=F3BSD37A8WSJQ', '_blank')}
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                data-testid="donate-building-fund-btn"
                            >
                                Donate to the Building Fund
                                <Heart className="w-5 h-5 ml-2" />
                            </Button>
                        </div>
                        <div className="relative">
                            <div className="absolute -inset-4 bg-gradient-to-r from-[#C9A227]/20 to-transparent rounded-2xl blur-2xl" />
                            <img 
                                src="https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/cgrfsyob_image.png"
                                alt="Recovery Room Brothers"
                                className="relative rounded-2xl w-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Founder Section */}
            <section className="py-24 px-6 bg-[#3D4435]">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            Meet the Founder
                        </p>
                        <h2 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display']">
                            Dr. Herman I. Pryor Jr., Ed.D.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        {/* Photo */}
                        <div className="lg:col-span-5">
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-br from-[#C9A227]/20 to-transparent rounded-2xl blur-xl" />
                                <img 
                                    src={FOUNDER_PHOTO}
                                    alt="Dr. Herman I. Pryor Jr."
                                    className="relative rounded-2xl w-full object-cover shadow-2xl"
                                />
                            </div>
                        </div>
                        {/* Bio */}
                        <div className="lg:col-span-7">
                            <div className="bg-[#4A5240] rounded-2xl p-8 border border-[#5A6350]">
                                <p className="text-[#A0A0A0] text-lg leading-relaxed mb-6">
                                    The Recovery Room was founded in 2023 with a singular purpose: to create a safe, intentional space where Black men can heal and build meaningful community. With a background spanning counseling, behavioral and organizational psychology, leadership development, change management, adult education, and workforce transformation, Dr. Pryor brings a thoughtful blend of compassion, vision, and expertise to advancing the mission of supporting Black men in becoming the best versions of themselves.
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <Button
                                        variant="outline"
                                        className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-[#3D4435]"
                                        onClick={() => window.open('https://www.linkedin.com/in/peopleequityinvestor/', '_blank')}
                                        data-testid="founder-linkedin-btn"
                                    >
                                        <Linkedin className="w-4 h-4 mr-2" />
                                        LinkedIn
                                    </Button>
                                    <Button
                                        variant="outline"
                                        className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227] hover:text-[#3D4435]"
                                        onClick={() => window.open('https://www.hipenterprise.com/', '_blank')}
                                        data-testid="founder-website-btn"
                                    >
                                        <Globe className="w-4 h-4 mr-2" />
                                        Website
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Join Section */}
            <section id="application-section" className="py-24 px-6 bg-[#4A5240]">
                <div className="max-w-2xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            Join Us
                        </p>
                        <h2 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display'] mb-6">
                            Become a Member
                        </h2>
                        <p className="text-[#A0A0A0] leading-relaxed max-w-lg mx-auto">
                            Create your member account to access the full community experience - connect with brothers, share events, and build meaningful relationships.
                        </p>
                    </div>

                    <Card className="bg-[#3D4435] border-[#5A6350] shadow-2xl overflow-hidden">
                        <div className="h-1 bg-gradient-to-r from-[#C9A227] to-[#E8D48A]" />
                        <CardContent className="p-8 text-center">
                            <div className="w-20 h-20 rounded-full bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-6">
                                <Users className="w-10 h-10 text-[#C9A227]" />
                            </div>
                            <h3 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-6">
                                Join the Brotherhood
                            </h3>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button
                                    size="lg"
                                    onClick={() => navigate('/member/register')}
                                    className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold px-8"
                                    data-testid="create-account-btn"
                                >
                                    Create Your Account
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                                <Button
                                    size="lg"
                                    variant="outline"
                                    onClick={() => navigate('/member/login')}
                                    className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                                    data-testid="member-signin-btn"
                                >
                                    Already a Member? Sign In
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            <MainFooter />
        </div>
    );
};

export default JoinPage;
